/**
 * Color normalization for wide-gamut display compatibility.
 *
 * On wide-gamut displays, browsers return CSS computed values in
 * color(display-p3 R G B) format instead of hex/rgb. Libraries like
 * Mermaid.js only support hex/rgb/hsl, so we convert display-p3 to sRGB hex.
 *
 * Display P3 and sRGB share the same transfer function (sRGB gamma curve)
 * but have different RGB primaries. Conversion requires a matrix transform
 * in linear light space.
 */

// --- Transfer functions (sRGB gamma, shared by Display P3 and sRGB) ---

function srgbToLinear(v: number): number {
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

function linearToSrgb(v: number): number {
  return v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

function toHex(n: number): string {
  return Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');
}

// Display P3 to linear sRGB matrix (pre-composed from P3→XYZ(D65)→sRGB)
const P3_TO_SRGB_R = [1.2249401, -0.2249402, 0.0000001] as const;
const P3_TO_SRGB_G = [-0.0420569, 1.0420571, -0.0000002] as const;
const P3_TO_SRGB_B = [-0.0196376, -0.0786361, 1.0982735] as const;

/**
 * Convert a display-p3 color string to sRGB hex.
 * Returns the input unchanged if it's not a display-p3 color.
 *
 * Safe to call on any string — non-color values like "1", "8px", "#ff0000"
 * pass through unchanged.
 */
export function normalizeColor(value: string): string {
  const match = value.match(
    /^color\(display-p3\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*[\d.]+)?\)$/
  );
  if (!match) return value;

  const p3r = parseFloat(match[1] ?? '0');
  const p3g = parseFloat(match[2] ?? '0');
  const p3b = parseFloat(match[3] ?? '0');

  // 1. Undo sRGB gamma to get linear Display P3
  const lr = srgbToLinear(p3r);
  const lg = srgbToLinear(p3g);
  const lb = srgbToLinear(p3b);

  // 2. Apply Display P3 → linear sRGB matrix
  const sr = P3_TO_SRGB_R[0] * lr + P3_TO_SRGB_R[1] * lg + P3_TO_SRGB_R[2] * lb;
  const sg = P3_TO_SRGB_G[0] * lr + P3_TO_SRGB_G[1] * lg + P3_TO_SRGB_G[2] * lb;
  const sb = P3_TO_SRGB_B[0] * lr + P3_TO_SRGB_B[1] * lg + P3_TO_SRGB_B[2] * lb;

  // 3. Apply sRGB gamma, clamp, and convert to 0-255
  const r = Math.round(linearToSrgb(clamp01(sr)) * 255);
  const g = Math.round(linearToSrgb(clamp01(sg)) * 255);
  const b = Math.round(linearToSrgb(clamp01(sb)) * 255);

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Parse a color string (hex, rgb, rgba, or display-p3) to RGB 0-255 values.
 * Returns null if the format is unrecognized.
 */
export function parseColorToRgb(color: string): { r: number; g: number; b: number } | null {
  // Normalize display-p3 to hex first
  const normalized = normalizeColor(color);

  // Handle hex colors
  const hexMatch = normalized.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (hexMatch && hexMatch[1] && hexMatch[2] && hexMatch[3]) {
    return {
      r: parseInt(hexMatch[1], 16),
      g: parseInt(hexMatch[2], 16),
      b: parseInt(hexMatch[3], 16),
    };
  }

  // Handle rgb/rgba colors
  const rgbMatch = normalized.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (rgbMatch && rgbMatch[1] && rgbMatch[2] && rgbMatch[3]) {
    return {
      r: parseInt(rgbMatch[1], 10),
      g: parseInt(rgbMatch[2], 10),
      b: parseInt(rgbMatch[3], 10),
    };
  }

  return null;
}
